import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const INPUT =
  'w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-oasis-500 focus:outline-none focus:ring-2 focus:ring-oasis-200 transition bg-white'

export default function ChurchesTab({ showToast }) {
  const { t } = useTranslation()
  const { canEdit } = useAuth()
  const editable = canEdit()

  const [churches, setChurches] = useState([])
  const [loading, setLoading] = useState(true)
  const [newName, setNewName] = useState('')
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [editingName, setEditingName] = useState('')
  const [confirmingDeleteId, setConfirmingDeleteId] = useState(null)

  async function load() {
    const { data, error } = await supabase
      .from('churches')
      .select('*')
      .order('sort_order', { ascending: true })
      .order('name', { ascending: true })
    if (error) showToast(error.message, 'error')
    setChurches(data ?? [])
    setLoading(false)
  }

  async function addChurch() {
    const name = newName.trim()
    if (!name) return
    if (churches.some(c => c.name.toLowerCase() === name.toLowerCase())) {
      showToast(t('admin.churchDuplicate'), 'error')
      return
    }
    setSaving(true)
    const maxOrder = churches.reduce((m, c) => Math.max(m, c.sort_order ?? 0), 0)
    const { error } = await supabase.from('churches').insert({ name, sort_order: maxOrder + 1 })
    setSaving(false)
    if (error) { showToast(error.message, 'error'); return }
    setNewName('')
    showToast(t('admin.churchToastAdded'), 'success')
    load()
  }

  async function saveEdit(id) {
    const name = editingName.trim()
    if (!name) return
    const { error } = await supabase.from('churches').update({ name }).eq('id', id)
    if (error) { showToast(error.message, 'error'); return }
    setEditingId(null)
    setEditingName('')
    showToast(t('admin.churchToastUpdated'), 'success')
    load()
  }

  async function toggleActive(church) {
    const { error } = await supabase
      .from('churches')
      .update({ is_active: !church.is_active })
      .eq('id', church.id)
    if (error) { showToast(error.message, 'error'); return }
    load()
  }

  async function remove(id) {
    const { error } = await supabase.from('churches').delete().eq('id', id)
    setConfirmingDeleteId(null)
    if (error) { showToast(error.message, 'error'); return }
    showToast(t('admin.churchToastDeleted'), 'success')
    load()
  }

  // Reassign sequential sort_order across the whole list so ordering is stable
  // even when existing rows share a sort_order value.
  async function move(index, dir) {
    const target = index + dir
    if (target < 0 || target >= churches.length) return
    const arr = [...churches]
    ;[arr[index], arr[target]] = [arr[target], arr[index]]
    setChurches(arr)
    const { error } = await supabase.from('churches').upsert(
      arr.map((c, i) => ({ id: c.id, name: c.name, sort_order: i })),
    )
    if (error) showToast(error.message, 'error')
    load()
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-4 border-oasis-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-gray-900">{t('admin.churchesTitle')}</h2>
        <p className="mt-1 text-sm text-gray-500">{t('admin.churchesDesc')}</p>
      </div>

      {/* Add new church */}
      {editable && (
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            maxLength={100}
            placeholder={t('admin.churchNamePlaceholder')}
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addChurch() }}
            className={INPUT}
          />
          <button
            onClick={addChurch}
            disabled={saving || !newName.trim()}
            className="shrink-0 rounded-lg bg-oasis-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-oasis-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {saving ? t('admin.churchAddingBtn') : t('admin.churchAddBtn')}
          </button>
        </div>
      )}

      {/* List */}
      {churches.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center text-sm text-gray-400">
          {t('admin.churchesEmpty')}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100 overflow-hidden">
          {churches.map((church, index) => (
            <div key={church.id} className="flex items-center gap-3 px-4 py-3">
              {/* Reorder */}
              {editable && (
                <div className="flex flex-col">
                  <button
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    className="text-gray-400 hover:text-gray-700 disabled:opacity-30 leading-none"
                    aria-label={t('admin.churchMoveUp')}
                  >▲</button>
                  <button
                    onClick={() => move(index, 1)}
                    disabled={index === churches.length - 1}
                    className="text-gray-400 hover:text-gray-700 disabled:opacity-30 leading-none"
                    aria-label={t('admin.churchMoveDown')}
                  >▼</button>
                </div>
              )}

              {/* Name (editable inline) */}
              <div className="flex-1 min-w-0">
                {editingId === church.id ? (
                  <input
                    type="text"
                    maxLength={100}
                    value={editingName}
                    onChange={e => setEditingName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') saveEdit(church.id)
                      if (e.key === 'Escape') { setEditingId(null); setEditingName('') }
                    }}
                    autoFocus
                    className={INPUT}
                  />
                ) : (
                  <span className={`text-sm ${church.is_active ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                    {church.name}
                  </span>
                )}
              </div>

              {/* Inactive badge */}
              {!church.is_active && editingId !== church.id && (
                <span className="shrink-0 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">
                  {t('admin.churchInactive')}
                </span>
              )}

              {/* Actions */}
              {editable && (
                <div className="flex items-center gap-2 shrink-0">
                  {editingId === church.id ? (
                    <>
                      <button
                        onClick={() => saveEdit(church.id)}
                        className="text-xs font-medium rounded-lg bg-oasis-500 px-3 py-1.5 text-white hover:bg-oasis-700 transition"
                      >
                        {t('admin.churchSaveBtn')}
                      </button>
                      <button
                        onClick={() => { setEditingId(null); setEditingName('') }}
                        className="text-xs font-medium rounded-lg border border-gray-300 px-3 py-1.5 text-gray-600 hover:bg-gray-50 transition"
                      >
                        {t('admin.churchCancelBtn')}
                      </button>
                    </>
                  ) : confirmingDeleteId === church.id ? (
                    <>
                      <button
                        onClick={() => remove(church.id)}
                        className="text-xs font-medium rounded-lg bg-red-600 px-3 py-1.5 text-white hover:bg-red-700 transition"
                      >
                        {t('admin.churchConfirmDeleteBtn')}
                      </button>
                      <button
                        onClick={() => setConfirmingDeleteId(null)}
                        className="text-xs font-medium rounded-lg border border-gray-300 px-3 py-1.5 text-gray-600 hover:bg-gray-50 transition"
                      >
                        {t('admin.churchCancelBtn')}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => toggleActive(church)}
                        className="text-xs font-medium rounded-lg border border-gray-300 px-3 py-1.5 text-gray-600 hover:bg-gray-50 transition"
                      >
                        {church.is_active ? t('admin.churchHideBtn') : t('admin.churchShowBtn')}
                      </button>
                      <button
                        onClick={() => { setEditingId(church.id); setEditingName(church.name) }}
                        className="text-xs font-medium rounded-lg border border-gray-300 px-3 py-1.5 text-gray-600 hover:bg-gray-50 transition"
                      >
                        {t('admin.editBtn')}
                      </button>
                      <button
                        onClick={() => setConfirmingDeleteId(church.id)}
                        className="text-xs font-medium rounded-lg border border-red-300 px-3 py-1.5 text-red-600 hover:bg-red-50 transition"
                      >
                        {t('admin.deleteBtn')}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
